import React from 'react';
import { Check } from 'lucide-react';
import { useEffect, useRef } from 'react';

interface Step {
  id: string;
  label: string;
}

interface StepPosition {
  left: number;
  width: number;
}

interface BookingStepperProps {
  steps: Step[];
  currentStep: string;
  className?: string;
}

const BookingStepper: React.FC<BookingStepperProps> = ({ steps, currentStep, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const currentIndex = steps.findIndex(step => step.id === currentStep);
  const nextStep = currentIndex < steps.length - 1 ? steps[currentIndex + 1] : null;

  useEffect(() => {
    const container = containerRef.current;
    const activeStep = stepRefs.current[currentIndex];

    if (!container || !activeStep) return;

    const containerWidth = container.offsetWidth;
    const stepWidth = activeStep.offsetWidth;
    const stepLeft = activeStep.offsetLeft;

    // On desktop (>= 768px), no scrolling needed
    if (window.innerWidth >= 768) {
      container.scrollLeft = 0;
      return;
    }

    // On mobile, center the active step
    const scrollLeft = stepLeft - (containerWidth - stepWidth) / 2;
    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  }, [currentStep]);

  return (
    <div 
      ref={containerRef}
      className={`mt-2 bg-white rounded-xl transition-all duration-500 ease-in-out ${className}`}
    >
      {/* Desktop Stepper */}
      <div className="hidden md:flex items-center justify-between py-4">
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            {/* Step Circle */}
            <div 
              ref={el => stepRefs.current[index] = el}
              className={`flex flex-col items-center min-w-[120px] transition-all duration-500 ease-in-out ${
                index === currentIndex ? 'transform scale-105' : ''
              }`}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                  index < currentIndex
                    ? 'bg-[#E85C0D] border-[#E85C0D] text-black/20'
                    : index === currentIndex
                    ? 'border-[#E85C0D] text-[#E85C0D]'
                    : 'border-black/20 text-black/20'
                } transition-all duration-500 ease-in-out`}
              >
                {index < currentIndex ? (
                  <Check className="w-5 h-5 text-white" />
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
              <span
                className={`mt-2 text-sm whitespace-nowrap ${
                  index <= currentIndex ? 'text-[#E85C0D]' : 'text-[#2A382A]/20'
                } transition-all duration-500 ease-in-out`}
              >
                {step.label}
              </span>
            </div>

            {/* Connector Line */}
            {index < steps.length - 1 && (
              <div
                className={`h-px w-16 mx-2 ${
                  index < currentIndex ? 'bg-[#E85C0D]' : 'bg-black/20'
                } transition-all duration-500 ease-in-out`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      
      {/* Mobile Radial Stepper */}
      <div className="md:hidden flex items-center justify-between p-4">
        <div className="flex-1">
          <h3 className="text-lg font-medium text-[#2A382A] mb-1">
            {steps[currentIndex].label}
          </h3>
          {nextStep && (
            <p className="text-sm text-[#2A382A]/60">
              Next: {nextStep.label}
            </p>
          )}
        </div>
        <div className="relative flex items-center justify-center">
          <svg className="w-16 h-16 transform -rotate-90">
            <circle
              cx="32"
              cy="32"
              r="28"
              fill="none"
              stroke="#EFEFEF"
              strokeWidth="4"
            />
            <circle
              cx="32"
              cy="32"
              r="28"
              fill="none"
              stroke="#E85C0D"
              strokeWidth="4"
              strokeDasharray={`${(currentIndex + 1) * (175.9 / steps.length)} 175.9`}
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-base font-medium text-[#2A382A]">
            {currentIndex + 1} of {steps.length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default BookingStepper;